import type { components } from "@/api-schema";
import { BranchIdentifierSystem } from "@/api-schema-enums";
import { formatDateToLocale } from "@/common/helpers/format-date";
import { SystemIdRow } from "@/components/detail-page/_components/system-id-row";
import { DetailTabCard } from "@/components/detail-page/_components/tab/detail-tab-card";
import { DetailTabGrid } from "@/components/detail-page/_components/tab/detail-tab-grid";
import { getBranchIdentifier } from "@/modules/masterdata/branch/branch-identifiers";
import { FingerprintIcon, HashIcon } from "lucide-react";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";

type Branch = components["schemas"]["Branch"];

const BRANCH_IDENTIFIER_LABELS = [
  { system: BranchIdentifierSystem.V1, labelKey: "masterdata:V1ID" },
  { system: BranchIdentifierSystem.Formel1Ahg, labelKey: "masterdata:Formel1AhgIdentifier" },
  { system: BranchIdentifierSystem.Formel1Ghg, labelKey: "masterdata:Formel1GhgIdentifier" },
  { system: BranchIdentifierSystem.CROSS, labelKey: "masterdata:CrossIdentifier" },
  { system: BranchIdentifierSystem.FAHRSCHULE, labelKey: "masterdata:FahrschuleIdentifier" },
  { system: BranchIdentifierSystem.Sap, labelKey: "masterdata:SapIdentifier" },
  { system: BranchIdentifierSystem.CARANO, labelKey: "masterdata:CaranoIdentifier" },
] as const;

export function SystemIdsTab({ branch }: { branch: Branch }): ReactElement {
  const { t } = useTranslation(["masterdata", "common"]);

  return (
    <DetailTabGrid>
      <DetailTabCard icon={FingerprintIcon} title={t("common:External")}>
        <div className="flex flex-col gap-1">
          {BRANCH_IDENTIFIER_LABELS.map(({ system, labelKey }) => (
            <SystemIdRow
              key={system}
              label={t(labelKey)}
              value={getBranchIdentifier(branch, system) ?? undefined}
              mono
              copyable
            />
          ))}
        </div>
      </DetailTabCard>

      <DetailTabCard icon={HashIcon} title={t("common:Internal")}>
        <div className="flex flex-col gap-1">
          <SystemIdRow label={t("common:UUID")} value={branch.uuid} mono copyable />
          <SystemIdRow label={t("common:TechnicalId")} value={branch.technicalId ?? undefined} mono copyable />
          <SystemIdRow
            label={t("common:CreatedAt")}
            value={branch.createdAt ? formatDateToLocale(branch.createdAt) : undefined}
          />
          <SystemIdRow
            label={t("common:UpdatedAt")}
            value={branch.updatedAt ? formatDateToLocale(branch.updatedAt) : undefined}
          />
          {branch.deletedAt && <SystemIdRow label={t("common:Deleted")} value={formatDateToLocale(branch.deletedAt)} />}
        </div>
      </DetailTabCard>
    </DetailTabGrid>
  );
}
